import type { CSSProperties, ReactNode } from "react";
import { Icon } from "../../../kit";

/**
 * The preset panels' vocabulary — measured off the approved mockup
 * (2026-09-29): 12px/600 section labels, 30px part chips, 40px round metal
 * swatches with their names, 10px-radius tiles, one gold accent for "chosen".
 * Every preset panel builds from these, so they cannot drift apart.
 */

export function SectionLabel({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="text-[12px] font-semibold leading-4 text-ed-text-2">{children}</span>
      {aside}
    </div>
  );
}

export function Section({ label, aside, children, className = "" }: { label: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`mb-5 ${className}`}>
      <SectionLabel aside={aside}>{label}</SectionLabel>
      {children}
    </section>
  );
}

export function Chip({
  on,
  onClick,
  children,
  title,
  ...data
}: {
  on?: boolean;
  onClick: () => void;
  children: ReactNode;
  title?: string;
  [k: `data-${string}`]: string | boolean | undefined;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-pressed={!!on}
      className={`h-[30px] max-w-full shrink-0 truncate rounded-lg border px-3 text-[12px] transition-colors ${
        on ? "border-ed-gold bg-ed-active font-semibold text-ed-text" : "border-ed-border font-medium text-ed-text-2 hover:border-ed-line hover:text-ed-text"
      }`}
      {...data}
    >
      {children}
    </button>
  );
}

/** A round sample with its name underneath — metals, stone colours, backgrounds. */
export function Swatch({
  on,
  label,
  fill,
  onClick,
  disabled,
  ...data
}: {
  on?: boolean;
  label: string;
  fill: string;
  onClick: () => void;
  disabled?: boolean;
  [k: `data-${string}`]: string | boolean | undefined;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-pressed={!!on}
      title={label}
      className={`flex min-w-0 flex-col items-center gap-[7px] rounded-[10px] px-0.5 pb-[9px] pt-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        on ? "bg-ed-active" : "hover:bg-ed-hover"
      }`}
      {...data}
    >
      <span
        className="h-10 w-10 shrink-0 rounded-full"
        style={{
          backgroundImage: fill.includes("gradient") ? fill : undefined,
          backgroundColor: fill.includes("gradient") ? undefined : fill,
          boxShadow: on ? "0 0 0 2px #fff, 0 0 0 4px #946f2e" : "inset 0 0 0 1px rgba(0,0,0,0.12)",
        }}
      />
      <span className="w-full text-center text-[12px] leading-[15px] text-ed-text">{label}</span>
    </button>
  );
}

/** A picture tile with a name — finishes, looks, shots, backgrounds. */
export function Tile({
  on,
  label,
  sub,
  thumb,
  thumbStyle,
  onClick,
  disabled,
  busy,
  tall,
  ...data
}: {
  on?: boolean;
  label: string;
  sub?: string;
  thumb?: ReactNode;
  thumbStyle?: CSSProperties;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  tall?: boolean;
  [k: `data-${string}`]: string | boolean | undefined;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-pressed={!!on}
      className={`group flex min-w-0 flex-col gap-1.5 rounded-[10px] border p-1.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        on ? "border-ed-gold bg-ed-active" : "border-ed-border hover:border-ed-line"
      }`}
      {...data}
    >
      <span
        className={`relative flex w-full items-center justify-center overflow-hidden rounded-md ${tall ? "h-[72px]" : "h-[46px]"}`}
        style={thumbStyle}
      >
        {thumb}
        {busy ? (
          <span className="absolute inset-0 grid place-items-center bg-white/60">
            <Icon name="progress_activity" size={18} className="animate-spin text-ed-text-2" />
          </span>
        ) : null}
      </span>
      <span className="px-0.5 text-center text-[12px] font-medium leading-[15px] text-ed-text">{label}</span>
      {sub ? <span className="-mt-1 px-0.5 text-center text-[11px] leading-[14px] text-ed-text-2">{sub}</span> : null}
    </button>
  );
}

/** A full-width action row: icon, words, optional detail, chevron or spinner. */
export function ActionRow({
  icon,
  label,
  sub,
  onClick,
  disabled,
  busy,
  trailing,
  ...data
}: {
  icon: string;
  label: string;
  sub?: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  trailing?: ReactNode;
  [k: `data-${string}`]: string | boolean | undefined;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      className="flex w-full items-center gap-3 rounded-[10px] border border-ed-border px-3 py-2.5 text-left transition-colors hover:border-ed-line disabled:cursor-not-allowed disabled:opacity-50"
      {...data}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-ed-panel-2 text-ed-text-2">
        <Icon name={busy ? "progress_activity" : icon} size={18} className={busy ? "animate-spin" : ""} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium leading-[17px] text-ed-text">{label}</span>
        {sub ? <span className="block text-[12px] leading-4 text-ed-text-2">{sub}</span> : null}
      </span>
      {trailing ?? <Icon name="chevron_right" size={18} className="shrink-0 text-ed-text-3" />}
    </button>
  );
}

export function Note({ children, tone }: { children: ReactNode; tone?: "error" | "ok" }) {
  return (
    <p
      className={`rounded-lg px-3 py-2 text-[12px] leading-[17px] ${
        tone === "error" ? "bg-ed-red/10 text-ed-red" : tone === "ok" ? "bg-ed-green/10 text-ed-green" : "bg-ed-panel-2 text-ed-text-2"
      }`}
      role={tone === "error" ? "alert" : undefined}
    >
      {children}
    </p>
  );
}

/** "#rrggbb" from a catalog colour number. */
export function hexOf(n: number): string {
  return `#${n.toString(16).padStart(6, "0")}`;
}

/** The lit-metal sphere swatch lives with the library's other sphere art. */
export { metalFill } from "../../library/sphereArt";
